import React, { useState } from "react";
import {
    Box,
    Button,
    TextField,
    IconButton,
    Typography,
    Paper,
    Tabs,
    Tab
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import UploadFileIcon from "@mui/icons-material/UploadFile";

import * as XLSX from "xlsx";

const SPLINE_NAMES = ["g1", "g2"];

const EMPTY_NODE = {
    x: "",
    y: "",
    y2: ""
};

const createEmptyNode = () => ({
    x: "",
    y: "",
    y2: ""
});

export default function Splines({
    splines = {},
    setSplines
}) {
    const [active, setActive] = useState(0);

    const splineName = SPLINE_NAMES[active];

    // ------------------------------------------------------------
    // Current spline
    // ------------------------------------------------------------
    const current =
        splines?.[splineName] || {
            nodes: [],
            length: 0
        };

    // ------------------------------------------------------------
    // Draft data is kept separately so that partially entered
    // values are not lost.
    // ------------------------------------------------------------
    const draft =
        splines?.[`${splineName}_draft`] ||
        (
            current.nodes?.length
                ? current.nodes.map(node => ({
                    x: node.x ?? "",
                    y: node.y ?? "",
                    y2: node.y2 ?? ""
                }))
                : [createEmptyNode()]
        );

    // ------------------------------------------------------------
    // Convert draft values into API spline structure.
    //
    // IMPORTANT:
    // Number 0 must be preserved.
    // ------------------------------------------------------------
    const update = (newDraft) => {
        const validNodes = newDraft
            .filter(node => {
                const x = String(node.x ?? "").trim();
                const y = String(node.y ?? "").trim();

                return x !== "" && y !== "";
            })
            .map(node => ({
                x: Number(node.x),
                y: Number(node.y),

                // Preserve 0.
                y2:
                    node.y2 === "" ||
                        node.y2 === null ||
                        node.y2 === undefined
                        ? 0
                        : Number(node.y2)
            }))
            .filter(node =>
                Number.isFinite(node.x) &&
                Number.isFinite(node.y) &&
                Number.isFinite(node.y2)
            );

        setSplines(prev => ({
            ...prev,

            [splineName]:
                validNodes.length > 0
                    ? {
                        nodes: validNodes,
                        length: validNodes.length
                    }
                    : null,

            [`${splineName}_draft`]: newDraft
        }));
    };

    // ------------------------------------------------------------
    // Update one cell
    // ------------------------------------------------------------
    const handleNodeChange = (
        index,
        field,
        value
    ) => {
        const next = draft.map((node, i) =>
            i === index
                ? {
                    ...node,
                    [field]: value
                }
                : node
        );

        update(next);
    };

    // ------------------------------------------------------------
    // Add node
    // ------------------------------------------------------------
    const addNode = () => {
        update([
            ...draft,
            createEmptyNode()
        ]);
    };

    // ------------------------------------------------------------
    // Delete node
    // ------------------------------------------------------------
    const removeNode = (index) => {
        let next = draft.filter(
            (_, i) => i !== index
        );

        if (next.length === 0) {
            next = [createEmptyNode()];
        }

        update(next);
    };

    // ------------------------------------------------------------
    // Excel-style paste
    // ------------------------------------------------------------
    const FIELD_ORDER = [
        "x",
        "y",
        "y2"
    ];

    const handleGridPaste =
        (rowIndex, startField) =>
            event => {
                const clipboardText =
                    event.clipboardData?.getData("text") ?? "";

                // Normal single-cell paste
                if (
                    !/[\t\n\r]/.test(
                        clipboardText
                    )
                ) {
                    return;
                }

                event.preventDefault();

                const startColumn =
                    FIELD_ORDER.indexOf(
                        startField
                    );

                const lines = clipboardText
                    .replace(/\r\n/g, "\n")
                    .replace(/\r/g, "\n")
                    .split("\n")
                    .filter(
                        line =>
                            line.trim() !== ""
                    );

                if (!lines.length) {
                    return;
                }

                const next = draft.map(node => ({
                    ...node
                }));

                // --------------------------------------------------------
                // Grow rows when pasted data contains more rows.
                // --------------------------------------------------------
                while (
                    next.length <
                    rowIndex + lines.length
                ) {
                    next.push(
                        createEmptyNode()
                    );
                }

                lines.forEach(
                    (line, lineIndex) => {
                        const cells =
                            line.split(
                                /[\t,]+/
                            );

                        cells.forEach(
                            (
                                cell,
                                cellOffset
                            ) => {
                                const targetColumn =
                                    startColumn +
                                    cellOffset;

                                if (
                                    targetColumn <
                                    0 ||
                                    targetColumn >=
                                    FIELD_ORDER.length
                                ) {
                                    return;
                                }

                                const targetRow =
                                    rowIndex +
                                    lineIndex;

                                const field =
                                    FIELD_ORDER[
                                    targetColumn
                                    ];

                                next[
                                    targetRow
                                ] = {
                                    ...next[
                                    targetRow
                                    ],
                                    [field]:
                                        String(
                                            cell ?? ""
                                        ).trim()
                                };
                            }
                        );
                    }
                );

                update(next);
            };

    // ------------------------------------------------------------
    // Upload CSV / TXT / XLS / XLSX
    // ------------------------------------------------------------
    const handleFile = file => {
        if (!file) {
            return;
        }

        const reader =
            new FileReader();

        reader.onload = event => {
            try {
                let lines = [];

                const extension =
                    file.name
                        .split(".")
                        .pop()
                        .toLowerCase();

                // ----------------------------------------------------
                // Excel
                // ----------------------------------------------------
                if (
                    extension === "xls" ||
                    extension === "xlsx"
                ) {
                    const data =
                        new Uint8Array(
                            event.target.result
                        );

                    const workbook =
                        XLSX.read(
                            data,
                            {
                                type: "array"
                            }
                        );

                    const sheet =
                        workbook.Sheets[
                        workbook
                            .SheetNames[0]
                        ];

                    lines =
                        XLSX.utils.sheet_to_json(
                            sheet,
                            {
                                header: 1,
                                defval: ""
                            }
                        );
                }

                // ----------------------------------------------------
                // CSV / TXT
                // ----------------------------------------------------
                else {
                    lines =
                        String(
                            event.target
                                .result || ""
                        )
                            .split(/\r?\n/)
                            .map(line =>
                                line.split(
                                    /[\t,]+/
                                )
                            );
                }

                // ----------------------------------------------------
                // Convert uploaded data into nodes
                // ----------------------------------------------------
                const parsed =
                    lines
                        .filter(row => {
                            if (
                                !row ||
                                row.length < 2
                            ) {
                                return false;
                            }

                            const x =
                                String(
                                    row[0] ??
                                    ""
                                ).trim();

                            const y =
                                String(
                                    row[1] ??
                                    ""
                                ).trim();

                            return (
                                x !== "" &&
                                y !== ""
                            );
                        })
                        .map(row => ({
                            x: String(
                                row[0] ??
                                ""
                            ).trim(),

                            y: String(
                                row[1] ??
                                ""
                            ).trim(),

                            y2:
                                row.length >
                                    2
                                    ? String(
                                        row[2] ??
                                        ""
                                    ).trim()
                                    : ""
                        }))
                        .filter(node => {
                            return (
                                node.x !== "" &&
                                node.y !== "" &&
                                Number.isFinite(
                                    Number(
                                        node.x
                                    )
                                ) &&
                                Number.isFinite(
                                    Number(
                                        node.y
                                    )
                                ) &&
                                (
                                    node.y2 ===
                                    "" ||
                                    Number.isFinite(
                                        Number(
                                            node.y2
                                        )
                                    )
                                )
                            );
                        });

                if (parsed.length > 0) {
                    update(parsed);
                }
            } catch (error) {
                console.error(
                    "Failed to parse spline file:",
                    error
                );
            }
        };

        if (
            /\.(xls|xlsx)$/i.test(
                file.name
            )
        ) {
            reader.readAsArrayBuffer(
                file
            );
        } else {
            reader.readAsText(file);
        }
    };

    return (
        <Paper
            elevation={0}
            sx={{
                border: "none"
            }}
        >
            {/* ========================================================
                SPLINE TABS
            ======================================================== */}
            <Tabs
                value={active}
                onChange={(
                    event,
                    value
                ) => {
                    setActive(value);
                }}
                sx={{
                    mb: 2,
                    borderBottom: 1,
                    borderColor:
                        "divider"
                }}
            >
                {SPLINE_NAMES.map(
                    name => (
                        <Tab
                            key={name}
                            label={`${name.toUpperCase()}${splines?.[
                                    name
                                ]
                                    ? ` (${splines[name].nodes.length})`
                                    : ""
                                }`}
                        />
                    )
                )}
            </Tabs>

            {/* ========================================================
                ACTION BUTTONS
            ======================================================== */}
            <Box
                display="flex"
                gap={1}
                mb={2}
            >
                <Button
                    variant="contained"
                    component="label"
                    startIcon={
                        <UploadFileIcon />
                    }
                >
                    Upload{" "}
                    {splineName.toUpperCase()}

                    <input
                        type="file"
                        hidden
                        accept=".csv,.txt,.xls,.xlsx"
                        onChange={event => {
                            handleFile(
                                event.target
                                    .files?.[0]
                            );

                            // Allow uploading
                            // the same file again.
                            event.target.value =
                                "";
                        }}
                    />
                </Button>

                <Button
                    variant="outlined"
                    startIcon={
                        <AddIcon />
                    }
                    onClick={addNode}
                >
                    Add Node
                </Button>
            </Box>

            {/* ========================================================
                NODE GRID
            ======================================================== */}
            <Box
                border={1}
                borderColor="grey.300"
                borderRadius={1}
                p={1}
            >
                {/* Header */}
                <Box
                    display="flex"
                    gap={1}
                    mb={1}
                >
                    <Typography
                        sx={{
                            flex: 1,
                            fontWeight: 600
                        }}
                    >
                        X
                    </Typography>

                    <Typography
                        sx={{
                            flex: 1,
                            fontWeight: 600
                        }}
                    >
                        Y
                    </Typography>

                    <Typography
                        sx={{
                            flex: 1,
                            fontWeight: 600
                        }}
                    >
                        Y2
                    </Typography>

                    <Box width={40} />
                </Box>

                {/* Rows */}
                {draft.map(
                    (node, index) => (
                        <Box
                            key={index}
                            display="flex"
                            gap={1}
                            mb={1}
                        >
                            <TextField
                                size="small"
                                sx={{
                                    flex: 1
                                }}
                                value={
                                    node.x ??
                                    ""
                                }
                                onChange={event =>
                                    handleNodeChange(
                                        index,
                                        "x",
                                        event
                                            .target
                                            .value
                                    )
                                }
                                onPaste={handleGridPaste(
                                    index,
                                    "x"
                                )}
                            />

                            <TextField
                                size="small"
                                sx={{
                                    flex: 1
                                }}
                                value={
                                    node.y ??
                                    ""
                                }
                                onChange={event =>
                                    handleNodeChange(
                                        index,
                                        "y",
                                        event
                                            .target
                                            .value
                                    )
                                }
                                onPaste={handleGridPaste(
                                    index,
                                    "y"
                                )}
                            />

                            <TextField
                                size="small"
                                sx={{
                                    flex: 1
                                }}
                                value={
                                    node.y2 ??
                                    ""
                                }
                                onChange={event =>
                                    handleNodeChange(
                                        index,
                                        "y2",
                                        event
                                            .target
                                            .value
                                    )
                                }
                                onPaste={handleGridPaste(
                                    index,
                                    "y2"
                                )}
                            />

                            <IconButton
                                size="small"
                                onClick={() =>
                                    removeNode(
                                        index
                                    )
                                }
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    )
                )}
            </Box>
        </Paper>
    );
}